import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { CATALOG_PRODUCTS_QUERY_KEY } from "./use-products-list";
import { CATALOG_PRICES_QUERY_KEY } from "./use-prices-list";
import { CATALOG_TAX_RATES_QUERY_KEY } from "./use-tax-rates-list";

/**
 * `POST /api/admin/catalog/refresh` is fire-and-forget on the API pod and
 * returns 204 No Content. Other pods refresh within their own 10-minute
 * TTL (per the API endpoint summary).
 *
 * The BO list queries call Stripe directly and don't depend on the public
 * tier-catalog cache, but a manual refresh signals "I want the latest" —
 * so we drop the local TanStack Query cache for products, prices, and
 * tax rates to force a refetch on next render.
 */
async function refreshCatalogCache(): Promise<void> {
  await httpClient<null>("/api/admin/catalog/refresh", { method: "POST" });
}

export function useRefreshCatalogCache() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refreshCatalogCache,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATALOG_PRODUCTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CATALOG_PRICES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CATALOG_TAX_RATES_QUERY_KEY });
    },
  });
}
